import axios from 'axios';
import { downscaleImage } from '@/utils/downscaleImage';
import getTeamsUrl from '../../../utils/getTeamsUrl';

// Photos and files on internal notes go to the private team attachments bucket
// through the teams service, never the public webchat buckets used for
// customer messages. A note stores only each file's S3 key; every view asks the
// teams service for a short-lived link, after the same access check as the
// customer's notes.

const teamsUrl = () => getTeamsUrl(window.location.href);

// Uploads one file and returns what the note stores: { key, name, type, size }.
export const uploadNoteAttachment = async (file, { _business, _customer, onProgress }) => {
  const uploadFile = await downscaleImage(file);

  const { data } = await axios.get(`${teamsUrl()}/api/conversation-notes/attachments/upload-url`, {
    params: { fileType: uploadFile.type, fileSize: uploadFile.size, _business, _customer },
    _suppressErrorToast: true,
  });

  if (!data?.uploadUrl || data.error) {
    throw new Error(data?.message || 'Your file could not be uploaded. Please try again.');
  }

  // S3 rejects the app-wide JWT header and credentials, and the type must
  // match the one the URL was signed for.
  await axios.put(data.uploadUrl, uploadFile, {
    headers: { 'Content-Type': data.contentType || uploadFile.type, Authorization: null },
    withCredentials: false,
    timeout: 0,
    _suppressErrorToast: true,
    onUploadProgress: (event) => {
      if (event.total) onProgress?.(Math.min(1, event.loaded / event.total));
    },
  });

  return { key: data.key, name: uploadFile.name || file.name, type: uploadFile.type, size: uploadFile.size };
};

// Links last an hour; reuse one for most of that.
const URL_REUSE_MS = 50 * 60 * 1000;
const signedUrls = new Map();

export const getNoteAttachmentUrl = (key, { name, download = false } = {}) => {
  const cacheKey = `${key}|${name || ''}|${download}`;
  const cached = signedUrls.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;

  const promise = axios
    .get(`${teamsUrl()}/api/conversation-notes/attachments/url`, {
      params: { key, name, download },
      _suppressErrorToast: true,
    })
    .then(({ data }) => {
      if (!data?.signedUrl) throw new Error('This file is unavailable.');
      return data.signedUrl;
    });

  signedUrls.set(cacheKey, { promise, expiresAt: Date.now() + URL_REUSE_MS });
  promise.catch(() => signedUrls.delete(cacheKey));
  return promise;
};

// Downloads under the uploaded name (the link is served as an attachment).
export const downloadNoteAttachment = async (attachment) => {
  const url = await getNoteAttachmentUrl(attachment.key, { name: attachment.name, download: true });
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const isImageAttachment = (attachment) => Boolean(attachment?.type?.startsWith('image/'));

// What a draft keeps across reloads: finished uploads only, without the
// in-memory preview.
export const storableNoteAttachments = (attachments = []) =>
  attachments
    .filter((attachment) => attachment.key && !attachment.loading)
    .map(({ path, key, name, type, size }) => ({ path, key, name, type, size }));
